import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import AppPreviewDialog from './AppPreviewDialog';
import { hiddenCurrentPictures, hiddenPicturesText } from './hostList';
import {
  CLEARANCE_NOTE, formatDate, otherUses, otherUsesLabel, removeTitle, sourceLabel, thumbnail, useArmedClick, usageLabel,
  whenLabel,
} from './pictureParts';

const STATUS_LABELS = { OPEN: 'Offen', REQUESTED: 'Angefragt', APPROVED: 'Erlaubt', DENIED: 'Abgelehnt' };

function PictureItem({ picture, agreed, busy, onClear, onRemove, onPreview }) {
  // Without the organiser's consent a picture is cleared only on a second click.
  const [armed, clear] = useArmedClick(() => onClear(picture, true), !agreed);
  const [removeArmed, remove] = useArmedClick(() => onRemove(picture));
  return (
    <li className={'irp-item' + (picture.cleared ? ' is-cleared' : '') + (busy ? ' is-busy' : '')}>
      <a className="irp-thumb" href={picture.image} target="_blank" rel="noreferrer" title="Bild in voller Größe öffnen">
        <img src={thumbnail(picture.image)} alt="" loading="lazy" />
      </a>
      <div className="irp-body">
        <strong className="irp-title" title={picture.title}>{picture.title}</strong>
        <span className="irp-meta">
          {whenLabel(picture)}
          {whenLabel(picture) ? ' · ' : ''}
          <span className={'irp-source irp-source--' + (picture.source === 'INSTAGRAM' ? 'ig' : 'web')}>{sourceLabel(picture)}</span>
        </span>
        <a className="irp-url" href={picture.image} target="_blank" rel="noreferrer" title={picture.image}>{picture.image}</a>
        {otherUses(picture).length > 0 && (
          <details className="irp-uses">
            <summary>{otherUsesLabel(picture)} – eine Freigabe gilt für alle</summary>
            <ul>
              {otherUses(picture).map((use) => <li key={use.id}>{usageLabel(use)}</li>)}
            </ul>
          </details>
        )}
        <div className="irp-foot">
          <span className={'ir-visibility' + (picture.cleared ? ' is-on' : '')}>
            {picture.cleared ? `Freigegeben${picture.clearedAt ? ` am ${formatDate(picture.clearedAt)}` : ''}` : 'Versteckt'}
          </span>
          {picture.listed && (
            <span className={'isl-badge isl-badge--' + picture.listed.toLowerCase()}>
              {picture.listed === 'WHITE' ? 'Whitelist' : 'Blacklist'} · {picture.listedBy}
            </span>
          )}
          <button type="button" className="pp-btn irp-preview" onClick={() => onPreview(picture)}>App-Vorschau</button>
          {picture.cleared ? (
            <button type="button" className="pp-btn" onClick={() => onClear(picture, false)} disabled={busy}>Verstecken</button>
          ) : picture.listed === 'BLACK' ? (
            <span className="isl-blocked">Blacklist – nicht freigebbar</span>
          ) : (
            <button type="button" className={'pp-btn irp-approve' + (armed ? ' pp-btn--armed' : '')} onClick={clear} disabled={busy}>
              {armed ? 'Ohne Zusage freigeben?' : 'Freigeben'}
            </button>
          )}
          <button type="button" className={'pp-btn ' + (removeArmed ? 'pp-btn--armed' : 'pp-btn--danger')}
            onClick={remove} disabled={busy} title={removeTitle(picture)}>
            {removeArmed ? 'Wirklich entfernen?' : 'Entfernen'}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Every picture of one organiser in a panel on the right: where each comes from, its
 * address, and whether it is cleared. Each picture is cleared on its own.
 */
export default function AccountPicturesPanel({ account, onClose, onChanged }) {
  const [pictures, setPictures] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const closeRef = useRef(null);
  const handle = account.handle || '';

  const load = useCallback(async () => {
    setError(null);
    try {
      setPictures(await ImageRightsAPI.pictures(handle || '-'));
    } catch (err) {
      console.error('Fehler beim Laden der Bilder:', err);
      setError('Konnte die Bilder nicht laden.');
    }
  }, [handle]);

  useEffect(() => { setPictures(null); load(); }, [load]);
  useEffect(() => { closeRef.current?.focus(); }, [handle]);
  useEffect(() => {
    // Esc closes the preview first, the panel only when no preview is open.
    const onKey = (event) => { if (event.key === 'Escape' && !preview) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, preview]);

  const setClearance = async (picture, cleared) => {
    setBusy((prev) => new Set(prev).add(picture.image));
    setError(null);
    try {
      const result = await ImageRightsAPI.clear([picture.image], cleared, cleared ? CLEARANCE_NOTE : null);
      if (cleared && result?.blocked) {
        setError('Dieses Bild stammt von einer Blacklist-Quelle und bleibt versteckt.');
        return;
      }
      // The same picture can stand on several markers.
      setPictures((prev) => prev && prev.map((each) => (each.image === picture.image
        ? { ...each, cleared, visibleInProd: cleared, clearedAt: cleared ? new Date().toISOString() : null }
        : each)));
      onChanged?.();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Konnte das Bild nicht speichern.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(picture.image);
        return next;
      });
    }
  };

  const removePicture = async (picture) => {
    setBusy((prev) => new Set(prev).add(picture.image));
    setError(null);
    try {
      await ImageRightsAPI.remove(picture.image);
      setPictures((prev) => prev && prev.filter((each) => each.image !== picture.image));
      onChanged?.();
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setError('Konnte das Bild nicht entfernen.');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(picture.image);
        return next;
      });
    }
  };

  const clearedCount = (pictures ?? []).filter((picture) => picture.cleared).length;

  // The hidden pictures as a list to send the organiser.
  const [listCopied, setListCopied] = useState(false);
  const hiddenCount = hiddenCurrentPictures(pictures).length;
  const copyList = async () => {
    const text = hiddenPicturesText(pictures);
    try {
      await navigator.clipboard.writeText(text);
      setListCopied(true);
      setTimeout(() => setListCopied(false), 2500);
    } catch {
      window.prompt('Liste kopieren:', text);
    }
  };

  return (
    <>
      <div className="irp-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="irp" role="dialog" aria-modal="true" aria-label={`Bilder von ${handle ? `@${handle}` : 'Events ohne Account'}`}>
        <header className="irp-head">
          <div className="irp-head-main">
            <h2 className="irp-handle">{handle ? `@${handle}` : 'Ohne Instagram-Account'}</h2>
            <p className="irp-sub">
              {handle && <span className={'irp-status irp-status--' + account.status.toLowerCase()}>{STATUS_LABELS[account.status]}</span>}
              {account.hostActive && <span className="irp-status irp-status--host">Host aktiv</span>}
              {pictures ? `${clearedCount} von ${pictures.length} ${pictures.length === 1 ? 'Bild' : 'Bildern'} freigegeben` : 'Lädt …'}
            </p>
          </div>
          <button ref={closeRef} type="button" className="irp-close" onClick={onClose} aria-label="Schließen">✕</button>
        </header>

        <p className="irp-hint">
          {account.agreed
            ? 'Der Veranstalter hat zugestimmt. Prüf trotzdem jedes Bild und gib es einzeln frei.'
            : 'Keine Zusage. Gib nur Bilder frei, in denen nichts Geschütztes steckt: reine Schrift auf schlichtem Hintergrund, kein Foto.'}
        </p>

        {pictures && (
          <div className="irp-tools">
            <button type="button" className="ir-export" onClick={copyList} disabled={hiddenCount === 0}
              title="Titel, Datum und Link aller noch versteckten Bilder – zum Einfügen in die DM an den Veranstalter">
              {listCopied ? `Kopiert ✓ (${hiddenCount})` : `Liste der versteckten Bilder kopieren (${hiddenCount})`}
            </button>
          </div>
        )}

        {error && <p className="pp-error">{error}</p>}

        {!pictures ? (
          <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
        ) : pictures.length === 0 ? (
          <div className="pp-empty">Keine Bilder von außen.</div>
        ) : (
          <ul className="irp-list">
            {pictures.map((picture) => (
              <PictureItem
                key={picture.image}
                picture={picture}
                agreed={account.agreed}
                busy={busy.has(picture.image)}
                onClear={setClearance}
                onRemove={removePicture}
                onPreview={setPreview}
              />
            ))}
          </ul>
        )}
      </aside>
      {preview && <AppPreviewDialog picture={preview} onClose={() => setPreview(null)} />}
    </>
  );
}
